/* =====================================================================
 * StorageFileCard — 단일 파일/폴더 카드 (그리드 뷰 전용)
 * ===================================================================== */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  File,
  FileAudio,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Folder,
  Presentation,
} from 'lucide-react';
import type { WorkspaceFile, WorkspaceItem, WorkspaceMode } from '@auw/workspace-utils';
import { WORKSPACE_MODES } from '@auw/workspace-utils';
import { getFileServeUrl, getFileThumbnailUrl } from '@auw/api-client';
import { authFetch } from '@auw/api-client';
import {
  StorageCard,
  CardPreview,
  CardThumbnail,
  CardVideoThumbnail,
  CardIconWrap,
  StorageCardBody,
  StorageCardTitle,
  CardMeta,
  PageCardBadge,
} from './storage.styles';

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const MIME_ICON_MAP: Record<string, { icon: React.ElementType; color: string }> = {
  image: { icon: FileImage, color: '#e879a0' },
  video: { icon: FileVideo, color: '#8b5cf6' },
  audio: { icon: FileAudio, color: '#f59e0b' },
  pdf: { icon: FileText, color: '#ef4444' },
  spreadsheet: { icon: FileSpreadsheet, color: '#10b981' },
  presentation: { icon: Presentation, color: '#f97316' },
  code: { icon: FileCode, color: '#3b82f6' },
  text: { icon: FileText, color: '#6b7280' },
  default: { icon: File, color: '#8e8ea0' },
};

function getMimeCategory(mime: string): string {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime === 'application/pdf') return 'pdf';
  if (
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    mime === 'text/csv'
  )
    return 'spreadsheet';
  if (mime.includes('presentation') || mime.includes('powerpoint'))
    return 'presentation';
  if (
    mime.includes('javascript') ||
    mime.includes('json') ||
    mime.includes('xml') ||
    mime.includes('html') ||
    mime.includes('css')
  )
    return 'code';
  if (mime.startsWith('text/')) return 'text';
  return 'default';
}

export function truncateFileName(name: string, max = 30): string {
  if (name.length <= max) return name;
  const dotIdx = name.lastIndexOf('.');
  if (dotIdx > 0) {
    const ext = name.slice(dotIdx);
    const baseMax = max - ext.length - 3;
    if (baseMax > 0) return name.slice(0, baseMax) + '...' + ext;
  }
  return name.slice(0, max - 3) + '...';
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function getMimeLabel(mime: string, t: (key: string) => string): string {
  const cat = getMimeCategory(mime);
  const ext = mime.split('/').pop()?.toUpperCase() ?? '';
  const labels: Record<string, string> = {
    image: `${ext} ${t('storage.image')}`,
    video: `${ext} ${t('storage.video')}`,
    audio: `${ext} ${t('storage.audio')}`,
    pdf: t('storage.pdfDoc'),
    spreadsheet: t('storage.spreadsheet'),
    presentation: t('storage.presentation'),
    code: t('storage.codeFile'),
    text: t('storage.text'),
    default: t('storage.file'),
  };
  return labels[cat] ?? t('storage.file');
}

/**
 * 파일 이름에서 확장자(대문자)만 추출. 확장자가 없거나 점으로 시작하는
 * 숨김 파일이면 '-' 를 반환한다. 저장소 테이블 "종류" 컬럼에서 사용.
 */
export function getExtensionLabel(originalName: string): string {
  if (!originalName) return '-';
  const idx = originalName.lastIndexOf('.');
  if (idx <= 0 || idx === originalName.length - 1) return '-';
  return originalName.slice(idx + 1).toUpperCase();
}

export function getSourceLabel(sourceType: string, t: (key: string) => string): string {
  const normalized = String(sourceType || '').toLowerCase();
  if (
    normalized === 'upload'
    || normalized.endsWith('_upload')
    || normalized === 'agent_upload'
  ) {
    return t('storage.upload');
  }
  if (
    normalized === 'agent_output'
    || normalized.endsWith('_output')
  ) {
    return t('storage.create');
  }
  return normalized || t('storage.other');
}

/* ------------------------------------------------------------------ */
/* Folder card                                                         */
/* ------------------------------------------------------------------ */

interface FolderCardProps {
  folder: WorkspaceItem;
  childCount?: number;
  selected: boolean;
  onClick: () => void;
  onDoubleClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

export const StorageFolderCard: React.FC<FolderCardProps> = ({
  folder,
  childCount,
  selected,
  onClick,
  onDoubleClick,
  onContextMenu,
}) => {
  const { t } = useTranslation('common');
  return (
    <StorageCard
      $selected={selected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
    >
      <CardPreview>
        <CardIconWrap $color="#3b82f6">
          <Folder size={24} />
        </CardIconWrap>
      </CardPreview>
      <StorageCardBody>
        <StorageCardTitle>{folder.title}</StorageCardTitle>
        <CardMeta>
          {childCount !== undefined ? t('storage.items', { count: childCount }) : t('storage.folder')}
        </CardMeta>
      </StorageCardBody>
    </StorageCard>
  );
};

/* ------------------------------------------------------------------ */
/* File card                                                           */
/* ------------------------------------------------------------------ */

interface FileCardProps {
  file: WorkspaceFile;
  selected: boolean;
  onClick: () => void;
  onDoubleClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

export const StorageFileCard: React.FC<FileCardProps> = ({
  file,
  selected,
  onClick,
  onDoubleClick,
  onContextMenu,
}) => {
  const cat = getMimeCategory(file.mime_type);
  const showsPreview = cat === 'image' || cat === 'video';
  const previewUrl = cat === 'video'
    ? getFileServeUrl(file.file_id)
    : getFileThumbnailUrl(file.file_id);
  const info = MIME_ICON_MAP[cat] ?? MIME_ICON_MAP.default;
  const Icon = info.icon;

  const [thumbUrl, setThumbUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!showsPreview) {
      setThumbUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }
    let revoked = false;
    authFetch(previewUrl)
      .then((res) => {
        if (!res.ok) throw new Error(`thumbnail ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (revoked) return;
        setThumbUrl(URL.createObjectURL(blob));
      })
      .catch(() => { /* silently fall back to icon */ });
    return () => {
      revoked = true;
      setThumbUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [previewUrl, showsPreview]);

  return (
    <StorageCard
      $selected={selected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onContextMenu={onContextMenu}
    >
      <CardPreview>
        {showsPreview && thumbUrl ? (
          cat === 'video' ? (
            <CardVideoThumbnail
              src={thumbUrl}
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <CardThumbnail
              src={thumbUrl}
              alt={file.original_name}
              loading="lazy"
            />
          )
        ) : (
          <CardIconWrap $color={info.color}>
            <Icon size={24} />
          </CardIconWrap>
        )}
      </CardPreview>
      <StorageCardBody>
        <StorageCardTitle title={file.original_name}>{truncateFileName(file.original_name)}</StorageCardTitle>
        <CardMeta>{formatBytes(file.size_bytes)}</CardMeta>
      </StorageCardBody>
    </StorageCard>
  );
};

/* ------------------------------------------------------------------ */
/* Page card (workspace page displayed as folder-like card in grid)   */
/* ------------------------------------------------------------------ */

interface PageCardProps {
  item: WorkspaceItem;
  fileCount?: number;
  selected: boolean;
  onClick: () => void;
  onDoubleClick: () => void;
}

export const StoragePageCard: React.FC<PageCardProps> = ({
  item,
  fileCount,
  selected,
  onClick,
  onDoubleClick,
}) => {
  const { t } = useTranslation('common');
  const modeInfo = WORKSPACE_MODES[item.mode as WorkspaceMode];
  const ModeIcon = modeInfo?.icon ?? FileText;
  const color = modeInfo?.color ?? '#6B9EC2';

  return (
    <StorageCard $selected={selected} onClick={onClick} onDoubleClick={onDoubleClick}>
      <CardPreview>
        <CardIconWrap $color={color}>
          <ModeIcon size={24} />
        </CardIconWrap>
        {fileCount !== undefined && fileCount > 0 && (
          <PageCardBadge>{t('storage.files', { count: fileCount })}</PageCardBadge>
        )}
      </CardPreview>
      <StorageCardBody>
        <StorageCardTitle>{item.title}</StorageCardTitle>
        <CardMeta>{modeInfo ? t(modeInfo.labelKey) : item.mode}</CardMeta>
      </StorageCardBody>
    </StorageCard>
  );
};
